/**
 * repo.contacts.ts —— 联系人仓储
 *
 * 数据流单向（架构约定）：**所有写操作都在这里落库，然后广播事件**，
 * UI 只订阅事件更新，绝不自己改数据。
 *
 * 所以每个写方法都是「写库 → emit」两步，顺序不能反：
 * 先写库再广播，能保证订阅者收到事件时读到的一定是新数据。
 */

import {
  STORE,
  dbGet,
  dbGetAll,
  dbPut,
  dbDelete,
  uid,
} from './db';
import { eventBus } from '@/core/event-bus';
import { toPinyinKey, matchesKeyword } from './pinyin';
import { sessionsRepo } from './repo.sessions';
import type { Contact } from '@/types/models';

/** 新建联系人时的输入。只有 name 必填 */
export interface ContactInput {
  name: string;
  remark?: string;
  avatar?: string;
  signature?: string;
}

/** 联系人列表的统一排序：按拼音键，同键时按创建时间稳定排列 */
function sortContacts(list: Contact[]): Contact[] {
  return list.sort((a, b) => {
    const byPinyin = a.pinyin.localeCompare(b.pinyin);
    if (byPinyin !== 0) return byPinyin;
    return a.createdAt - b.createdAt;
  });
}

export const contactsRepo = {
  /** 全部联系人，已按拼音排序 */
  async list(): Promise<Contact[]> {
    const all = await dbGetAll<Contact>(STORE.contacts);
    return sortContacts(all);
  },

  async get(id: string): Promise<Contact | undefined> {
    return dbGet<Contact>(STORE.contacts, id);
  },

  /** 按姓名精确查找（同名时取最早创建的那个） */
  async getByName(name: string): Promise<Contact | undefined> {
    const all = await dbGetAll<Contact>(STORE.contacts);
    const target = name.trim();
    return all
      .filter((c) => c.name === target)
      .sort((a, b) => a.createdAt - b.createdAt)[0];
  },

  /** 按姓名/备注/拼音模糊检索 */
  async search(keyword: string): Promise<Contact[]> {
    const all = await dbGetAll<Contact>(STORE.contacts);
    const hit = all.filter(
      (c) => matchesKeyword(c.name, keyword) || matchesKeyword(c.remark, keyword),
    );
    return sortContacts(hit);
  },

  async count(): Promise<number> {
    const all = await dbGetAll<Contact>(STORE.contacts);
    return all.length;
  },

  /**
   * 新建联系人。姓名重复时不拦截 —— 同名是现实情况（两个「张三」），
   * 强行去重会让用户困惑。需要"有就返回没有才建"时用 findOrCreate。
   */
  async create(input: ContactInput): Promise<Contact> {
    const name = input.name.trim();
    if (!name) throw new Error('联系人姓名不能为空');

    const contact: Contact = {
      id: uid(),
      name,
      remark: input.remark?.trim() ?? '',
      avatar: input.avatar ?? '',
      pinyin: toPinyinKey(name),
      signature: input.signature?.trim() ?? '',
      createdAt: Date.now(),
    };

    await dbPut(STORE.contacts, contact);
    eventBus.emit('contact:list-changed', {});
    eventBus.emit('contact:created', { contactId: contact.id, name: contact.name });
    return contact;
  },

  /** 有就返回、没有就新建。AI 最常用的入口 */
  async findOrCreate(name: string): Promise<Contact> {
    const existing = await this.getByName(name);
    if (existing) return existing;
    return this.create({ name });
  },

  /** 局部更新。改名时同步重算拼音键 */
  async update(id: string, patch: Partial<Omit<Contact, 'id' | 'createdAt'>>): Promise<Contact> {
    const current = await dbGet<Contact>(STORE.contacts, id);
    if (!current) throw new Error(`联系人不存在：${id}`);

    const next: Contact = { ...current, ...patch };
    // 名字变了就必须重算拼音，否则排序和检索会失准
    if (patch.name !== undefined) {
      next.name = patch.name.trim();
      next.pinyin = toPinyinKey(next.name);
    }

    await dbPut(STORE.contacts, next);
    eventBus.emit('contact:list-changed', {});
    eventBus.emit('contact:updated', { contactId: id });
    return next;
  },

  /**
   * 删除联系人。**同时删除其名下的会话与消息**，
   * 否则会话列表里会出现指向不存在联系人的孤儿条目。
   */
  async remove(id: string): Promise<void> {
    const sessions = await sessionsRepo.listByContact(id);
    for (const s of sessions) {
      await sessionsRepo.remove(s.id);
    }

    await dbDelete(STORE.contacts, id);
    eventBus.emit('contact:list-changed', {});
    eventBus.emit('contact:removed', { contactId: id });
  },
};
